// ============================================
// 날짜 resolver — aiGuide.js에서 책임 분리 (순수 이동)
// ============================================
// 날짜 토큰/템플릿 치환/기본기간 resolve 등 cross-cutting 날짜 유틸 모음.
// 무의존(순수). aiIntentionMeta/aiPromptBuilders가 본 모듈을 import.

// 오늘 날짜 시스템 메시지 생성 //
// RL-5 — ISO YYYY-MM-DD (KST). link-env <today>와 동일 포맷 → SQL/API 파라미터 직접 사용 안전.
// 결정성 + 영어 prompt 일관성. 이전: 한국어 "2026년 5월 18일".
export function getTodaySystemMessage() {
  // en-CA locale은 YYYY-MM-DD 포맷 강제 + Asia/Seoul timezone 명시 (UTC 차이 1일 회피)
  const today = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Seoul' });
  return `Today: ${today}`;
}

// 날짜 토큰 단일 진실원 (resolver map)
// LLM prompt 예시와 코드 측 SQL 치환이 동일한 resolver 사용 → drift 0
const _pad = (n) => String(n).padStart(2, '0');
const _fmtDate = (d) => `${d.getFullYear()}-${_pad(d.getMonth() + 1)}-${_pad(d.getDate())}`;

export const DATE_TOKEN_RESOLVERS = {
  today: (offset = 0) => {
    const d = new Date();
    d.setDate(d.getDate() + offset);
    return _fmtDate(d);
  },
  year: (offset = 0) => String(new Date().getFullYear() + offset),
};

// "{token[+-]N}" 패턴 → resolver 호출. 알 수 없는 token은 원문 보존
export function substituteDateTemplates(text) {
  if (!text || typeof text !== 'string') return text;
  return text.replace(/\{(\w+)([+-]\d+)?\}/g, (match, token, off) => {
    const resolver = DATE_TOKEN_RESOLVERS[token];
    if (!resolver) return match;
    const offset = off ? parseInt(off, 10) : 0;
    return resolver(offset);
  });
}

// ============================================
// defaultPeriod resolver — intention.defaultPeriod 토큰 → 실제 YYYY-MM-DD
// ============================================
// 토큰: 'today' | 'year_start' | 'this_week_start' | 'this_week_end' | '[+-]Nd'
// 사용처: call_api 호출 시 startDate/endDate 미지정 case에서 frontend가 자동 채움

function _formatYMD(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

function _addDays(d, days) {
  const nd = new Date(d);
  nd.setDate(nd.getDate() + days);
  return nd;
}

/**
 * @param {string} token — 'today' | 'year-start'|'year_start' | 'year-end' | 'month-start' | 'month-end' | 'this_week_start' | 'this_week_end' | '[+-]Nd'
 * @param {Date} [today]
 * @returns {string|null} YYYY-MM-DD or null if 토큰 인식 실패
 */
export function resolveDateToken(token, today = new Date()) {
  if (!token || typeof token !== 'string') return null;
  if (token === 'today') return _formatYMD(today);
  // year-start (hyphen) + year_start (underscore, backward compat)
  if (token === 'year-start' || token === 'year_start') return `${today.getFullYear()}-01-01`;
  if (token === 'year-end' || token === 'year_end') return `${today.getFullYear()}-12-31`;
  if (token === 'month-start' || token === 'month_start') {
    const m = String(today.getMonth() + 1).padStart(2, '0');
    return `${today.getFullYear()}-${m}-01`;
  }
  if (token === 'month-end' || token === 'month_end') {
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
    const m = String(today.getMonth() + 1).padStart(2, '0');
    return `${today.getFullYear()}-${m}-${String(lastDay).padStart(2, '0')}`;
  }
  if (token === 'this_week_start' || token === 'this_week_end') {
    const day = today.getDay();  // 0=Sun, 1=Mon, ... 6=Sat
    // 월요일 시작 (1=Mon)
    const offsetToMonday = day === 0 ? -6 : 1 - day;
    if (token === 'this_week_start') return _formatYMD(_addDays(today, offsetToMonday));
    return _formatYMD(_addDays(today, offsetToMonday + 6));  // 일요일
  }
  const m = /^([+-]?\d+)d$/.exec(token);
  if (m) return _formatYMD(_addDays(today, parseInt(m[1], 10)));
  return null;
}

/**
 * @param {object} intention — LOCAL_INTENTIONS_DATA.intentions[*] 원본
 * @param {Date} [today]
 * @returns {{startDate:string, endDate:string}|null}
 */
export function resolveDefaultPeriod(intention, today = new Date()) {
  if (!intention?.defaultPeriod) return null;
  const startDate = resolveDateToken(intention.defaultPeriod.start, today);
  const endDate = resolveDateToken(intention.defaultPeriod.end, today);
  if (!startDate || !endDate) return null;
  return { startDate, endDate };
}

// USER_TIME_KW_RE: 사용자가 explicit time keyword 입력 시 LLM dates 보존 신호
//   - 절대 시점: 오늘/어제/내일, 지난주/이번주/다음주/금주, 지난달/이번달/다음달/이달/금월, 작년/올해/내년/이번년, 당월/당주/당일
//   - 상대 기간: 최근 N일/N주/N달, 지난 N일/N주/N달, N일/N주/N달 전, 며칠/몇 주/몇 달 전
//   - 명시 날짜: N월 (1-12), N년, YYYY-MM-DD, N주차, N분기/N사분기, 상반기/하반기, 연초/연말/월초/월말/주초/주말
//   - 기간 표현: 사이/부터/까지/동안 + 시간 단어
export const USER_TIME_KW_RE = /오늘|어제|내일|지난주|이번주|다음주|금주|지난달|이번달|다음달|이달|금월|당월|당주|당일|작년|올해|내년|이번년|이번\s*해|올\s*해|지난\s*해|이전\s*해|최근\s*\d+|지난\s*\d+|\d+\s*일\s*전|\d+\s*주\s*전|\d+\s*달\s*전|며칠\s*전|몇\s*주\s*전|몇\s*달\s*전|\d{1,2}\s*월|\d{4}\s*년|\d{4}-\d{2}-\d{2}|\d+\s*주차|\d+\s*분기|\d+\s*사분기|상반기|하반기|연초|연말|월초|월말|주초|주말|사이|부터|까지|동안/;
